/**
 * Fly inside a frame: the Fly Agent marketplace app shows the agent in one
 * (AgentCrmPage, /agent-crm, 26 Oct 26).
 *
 * Google's sign in, the payment pages and the domain providers all refuse to
 * load in a frame, so inside one they open in a popup the tap itself opens
 * (an await first and the browser blocks it), and the popup hands its answer
 * back with postMessage before it closes: /agent/oauth-callback for Google,
 * /popup-return for a page that comes back to Fly (a payment). The frame keeps
 * its session and carries on where it was. Google's sign-in pages keep the
 * opener (their Cross-Origin-Opener-Policy is report-only, checked 26 Oct 26),
 * and both pages are on Fly's own origin, so the message is pinned to it.
 *
 * Outside a frame none of this runs and every page leaves as it always did.
 */

export const POPUP_MESSAGE = 'FLY_POPUP_RETURN';
export const POPUP_RETURN_PATH = '/popup-return';
const POPUP_NAME = 'fly_popup';
const POPUP_FEATURES = 'popup=yes,width=520,height=720';
const MARKETPLACE_KEY = 'fly-agent-crm-frame';

/** What a popup hands back. */
export type PopupAnswer =
  | { kind: 'google'; code: string; state: string }
  | { kind: 'google'; error: string; state: string | null }
  | { kind: 'page'; path: string };

/** True inside any frame; a frame that cannot even be asked counts as one. */
export function isFramed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

let marketplaceHere = false;

/** AgentCrmPage, once the marketplace has signed the person in: this frame is the marketplace's. */
export function markMarketplaceFrame(): void {
  marketplaceHere = true;
  try {
    sessionStorage.setItem(MARKETPLACE_KEY, '1');
  } catch {
    // Storage blocked in this frame: the flag above still holds for this page load.
  }
}

/**
 * Inside the frame the marketplace sign-in opened, on this page load or an
 * earlier one in the same frame. There the dashboard signs people in, so the
 * start page offers no Sign out and no sign-in of its own.
 */
export function isMarketplaceFrame(): boolean {
  if (!isFramed()) return false;
  if (marketplaceHere) return true;
  try {
    return sessionStorage.getItem(MARKETPLACE_KEY) === '1';
  } catch {
    return false;
  }
}

/** A blank popup, opened inside the tap. Null when the browser blocked it. */
export function openPopup(): Window | null {
  try {
    return window.open('', POPUP_NAME, POPUP_FEATURES);
  } catch {
    return null;
  }
}

/**
 * Send the popup to its page and wait for its answer. Null when the popup was
 * closed first (the person gave up) or could not be sent anywhere.
 */
export function popupAnswer(popup: Window, url: string): Promise<PopupAnswer | null> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (answer: PopupAnswer | null) => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMessage);
      window.clearInterval(watch);
      resolve(answer);
    };
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin || event.data?.type !== POPUP_MESSAGE || !event.data.answer) return;
      finish(event.data.answer as PopupAnswer);
    }
    window.addEventListener('message', onMessage);
    // The popup closes itself right after it answers, so a closed popup waits a beat for a message in flight.
    const watch = window.setInterval(() => {
      if (popup.closed) window.setTimeout(() => finish(null), 400);
    }, 500);
    try {
      popup.location.href = new URL(url, window.location.origin).toString();
    } catch {
      finish(null);
    }
  });
}

/** In a popup a Fly page opened: hand the answer to it. False when there is nobody to hand it to. */
export function handToOpener(answer: PopupAnswer): boolean {
  const opener = window.opener as Window | null;
  if (!opener || opener === window) return false;
  try {
    opener.postMessage({ type: POPUP_MESSAGE, answer }, window.location.origin);
    return true;
  } catch {
    return false;
  }
}

/** Where a payment page comes back to inside the popup; /popup-return hands `back` to the frame. */
export function popupReturnUrl(back: string): string {
  const u = new URL(back, window.location.origin);
  return `${window.location.origin}${POPUP_RETURN_PATH}?to=${encodeURIComponent(u.pathname + u.search)}`;
}

/**
 * The path /popup-return hands back: its ?to (an address on this site only)
 * with every other parameter the page that came back added (checkout=returned).
 */
export function popupReturnPath(search: string): string {
  const params = new URLSearchParams(search);
  const to = params.get('to') ?? '';
  params.delete('to');
  const safe = to.startsWith('/') && !to.startsWith('//') && !to.includes('\\') ? to : '/app';
  const u = new URL(safe, 'https://fly.invalid');
  params.forEach((value, key) => u.searchParams.set(key, value));
  return `${u.pathname}${u.search}`;
}

/** Open a page outside Fly: a new tab inside a frame, this tab otherwise. */
export function goOutside(url: string): void {
  if (isFramed()) window.open(url, '_blank', 'noopener,noreferrer');
  else window.location.assign(url);
}
